import { formatGel } from '@/utils';
import type {
    CategoryListItem,
    CategoryTreeNode,
    ProductListItem,
    ProductAmountRange,
    ProductType,
} from '@/api/models';
import type { NamedOption } from './namedOption';

/**
 * Presentational config + row shape + pure helpers for the Products table.
 *
 * The row shape is the bos-api's `ProductListItem` verbatim — the table is
 * wired to `useProductListQuery` and surfaces every field it returns. Logic
 * (the page hook) and render (the View) both read from here so the column ids,
 * filter option lists, and formatting rules can't drift between them.
 */

export type Product = ProductListItem;

/** Stable column ids — shared between the View columns and sort descriptor. */
export const PRODUCT_COLUMN = {
    name: 'name',
    sku: 'sku',
    type: 'type',
    category: 'category',
    brand: 'brand',
    variants: 'variants',
    quantity: 'quantity',
    cost: 'cost',
    price: 'price',
    stock: 'stock',
} as const;

export type ProductColumnId =
    (typeof PRODUCT_COLUMN)[keyof typeof PRODUCT_COLUMN];

/**
 * The grid fills the page from its top edge down to the app shell's bottom
 * padding (`py-6` on `#app-shell-main`), so the rows scroll inside the grid
 * and the page itself does not. With less room than the minimum, the grid
 * keeps the minimum and the page scrolls instead (BOS-193).
 */
export const PRODUCT_TABLE_BOTTOM_GAP_PX = 24;
export const PRODUCT_TABLE_MIN_HEIGHT_PX = 320;

/** SKU's width. Fixed because SKU is pinned from `sm` up; see below. */
export const PRODUCT_SKU_COLUMN_WIDTH = 140;

/**
 * Which columns stay put while the grid scrolls sideways (BOS-193): Name and
 * SKU from `sm` up, Name alone below it, where two pinned columns would leave
 * a phone almost nothing to scroll. Name narrows there for the same reason.
 *
 * Pinned columns take a fixed width: DataGrid offsets each pinned column by
 * the widths of the pinned columns before it, so a Name that grew with its
 * content would slide under SKU.
 */
export const getProductColumnPinning = (
    isNarrow: boolean,
): { pinned: ReadonlySet<ProductColumnId>; nameWidth: number } =>
    isNarrow
        ? { pinned: new Set([PRODUCT_COLUMN.name]), nameWidth: 160 }
        : {
              pinned: new Set([PRODUCT_COLUMN.name, PRODUCT_COLUMN.sku]),
              nameWidth: 280,
          };

/**
 * The 1-based rows the current page shows, for the footer's "Showing 51–100
 * of 180". `{ from: 0, to: 0 }` when nothing matched. A page past the end —
 * left over from a wider filter until the page resets — never reads as
 * starting after the last row.
 */
export const getPageRange = (
    page: number,
    pageSize: number,
    totalCount: number,
): { from: number; to: number } => {
    if (totalCount <= 0) {
        return { from: 0, to: 0 };
    }
    return {
        from: Math.min((page - 1) * pageSize + 1, totalCount),
        to: Math.min(page * pageSize, totalCount),
    };
};

/** Selectable product types for the type filter (order = display order). */
export const PRODUCT_TYPE_OPTIONS: ProductType[] = ['Simple', 'Variable'];

/**
 * A variant's label from its option value names, already in the product's
 * axis order: `Red / M`. The details page and the products export
 * (bos-app#120) label a variant through it.
 */
export const joinVariantOptionNames = (names: readonly string[]): string =>
    names.join(' / ');

/** Chip styling per product type. */
export const PRODUCT_TYPE_CHIP: Record<
    ProductType,
    { color: 'default' | 'accent'; labelKey: string }
> = {
    Simple: {
        color: 'default',
        labelKey: 'products.components.productTable.types.simple',
    },
    Variable: {
        color: 'accent',
        labelKey: 'products.components.productTable.types.variable',
    },
};

/** Sentinel option id meaning "no filter" in the Select dropdowns. */
export const ALL_FILTER_VALUE = '__all__';

/** Page sizes offered by the rows-per-page control (server `pageSize`, max 200). */
export const PRODUCT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/** Default server page size (matches the bos-api `GET /products` default). */
export const PRODUCT_DEFAULT_PAGE_SIZE = 50;

/** A flattened category option for the category filter, indented by `depth`. */
export type CategoryFilterOption = NamedOption & {
    depth: number;
};

/**
 * Flatten the nested category tree into a depth-tagged option list (pre-order),
 * so the filter Select can indent nested categories like the category picker.
 */
export const flattenCategoryOptions = (
    nodes: CategoryTreeNode[],
    depth = 0,
): CategoryFilterOption[] =>
    nodes.flatMap((node) => [
        { id: node.id, name: node.name, depth },
        ...flattenCategoryOptions(node.children, depth + 1),
    ]);

/**
 * Flatten the **flat** `/categories/picker` list (each item carries `parentId`)
 * into the same depth-tagged, pre-order option list as
 * {@link flattenCategoryOptions} — by rebuilding the hierarchy from `parentId`
 * and ordering each level by name. Lets dropdowns/filters use the dedicated
 * unpaginated picker endpoint instead of the heavier `/categories/tree`.
 */
export const flattenPickerCategories = (
    items: CategoryListItem[],
): CategoryFilterOption[] => {
    const childrenByParent = new Map<string | null, CategoryListItem[]>();
    for (const item of items) {
        const siblings = childrenByParent.get(item.parentId) ?? [];
        siblings.push(item);
        childrenByParent.set(item.parentId, siblings);
    }
    for (const siblings of childrenByParent.values()) {
        siblings.sort((a, b) => a.name.localeCompare(b.name));
    }

    const out: CategoryFilterOption[] = [];
    const walk = (parentId: string | null, depth: number): void => {
        for (const node of childrenByParent.get(parentId) ?? []) {
            out.push({ id: node.id, name: node.name, depth });
            walk(node.id, depth + 1);
        }
    };
    walk(null, 0);
    return out;
};

/** Render an integer-tetri amount through the shared GEL formatter. */
export const formatAmount = (valueTetri: number): string =>
    formatGel(valueTetri);

/**
 * Render a variant price (or cost) span as `₾ min – max`, collapsing to a
 * single value when the product's variants all share one.
 *
 * BOS-162: the column used to render `25.00 – 27.00` with no unit at all, which
 * beside a quantity column is a number of nothing. The symbol is carried once,
 * on the lower bound — `₾ 25.00 – 27.00` rather than `₾ 25.00 – ₾ 27.00`, which
 * says "lari" twice about one span and crowds a 140px column.
 */
export const formatAmountRange = (range: ProductAmountRange): string => {
    if (range.minTetri === range.maxTetri) {
        return formatGel(range.minTetri);
    }
    return `${formatGel(range.minTetri)} – ${formatGel(range.maxTetri, {
        withSymbol: false,
    })}`;
};

/**
 * Compute the page numbers to render in a pagination control, collapsing long
 * ranges with `'ellipsis'` markers (first/last always shown, plus a window
 * around the current page). Pure — unit-tested directly.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};
